import type {
  ConnectionMetrics,
  LiveConnectionStats,
  WebRTCStatsData,
} from 'features/analytics/interfaces';

export const getLatestConnectionMetrics = (
  statsData: WebRTCStatsData | null | undefined
): ConnectionMetrics | null => {
  if (!statsData) {
    return null;
  }

  const inboundVideo = statsData.video?.inbound?.[0];
  const inboundAudio = statsData.audio?.inbound?.[0];
  const remoteInboundVideo = statsData.remote?.video?.inbound?.[0];
  const remoteInboundAudio = statsData.remote?.audio?.inbound?.[0];
  const outboundVideo = statsData.video?.outbound?.[0];

  return {
    videoPacketsLost: inboundVideo?.packetsLost,
    videoPacketsReceived: inboundVideo?.packetsReceived,
    videoFramesPerSecond: inboundVideo?.framesPerSecond,
    videoBitrate: inboundVideo?.bitrate
      ? Math.round(inboundVideo.bitrate / 1000)
      : undefined,
    videoJitter: remoteInboundVideo?.jitter,
    videoRoundTripTime: remoteInboundVideo?.roundTripTime,
    audioPacketsLost: inboundAudio?.packetsLost,
    audioPacketsReceived: inboundAudio?.packetsReceived,
    audioJitter: remoteInboundAudio?.jitter,
    audioRoundTripTime: remoteInboundAudio?.roundTripTime,
    qualityLimitationReason: outboundVideo?.qualityLimitationReason,
    videoFramesDecoded: inboundVideo?.framesDecoded,
    videoFramesDropped: inboundVideo?.framesDropped,
    videoFramesReceived: inboundVideo?.framesReceived,
    videoFreezeCount: inboundVideo?.freezeCount,
    videoTotalFreezesDuration: inboundVideo?.totalFreezesDuration,
    videoPauseCount: inboundVideo?.pauseCount,
    videoTotalPausesDuration: inboundVideo?.totalPausesDuration,
    videoJitterBufferDelay: inboundVideo?.jitterBufferDelay,
    videoJitterBufferEmittedCount: inboundVideo?.jitterBufferEmittedCount,
  };
};

export const convertStatsObjectToCallStats = (
  statsData: WebRTCStatsData
): any => {
  const getFirstValue = (
    arr: any[] | undefined,
    prop: string,
    defaultValue = 0
  ) => arr?.[0]?.[prop] ?? defaultValue;

  return {
    inboundRtpVideo: {
      packetsLost: getFirstValue(statsData.video?.inbound, 'packetsLost'),
      packetsReceived: getFirstValue(
        statsData.video?.inbound,
        'packetsReceived'
      ),
      framesPerSecond: getFirstValue(
        statsData.video?.inbound,
        'framesPerSecond'
      ),
      framesDecoded: getFirstValue(statsData.video?.inbound, 'framesDecoded'),
      framesDropped: getFirstValue(statsData.video?.inbound, 'framesDropped'),
      framesReceived: getFirstValue(statsData.video?.inbound, 'framesReceived'),
      freezeCount: getFirstValue(statsData.video?.inbound, 'freezeCount'),
      totalFreezesDuration: getFirstValue(
        statsData.video?.inbound,
        'totalFreezesDuration'
      ),
      pauseCount: getFirstValue(statsData.video?.inbound, 'pauseCount'),
      totalPausesDuration: getFirstValue(
        statsData.video?.inbound,
        'totalPausesDuration'
      ),
      jitterBufferDelay: getFirstValue(
        statsData.video?.inbound,
        'jitterBufferDelay'
      ),
      jitterBufferEmittedCount: getFirstValue(
        statsData.video?.inbound,
        'jitterBufferEmittedCount'
      ),
    },
    inboundRtpAudio: {
      packetsLost: getFirstValue(statsData.audio?.inbound, 'packetsLost'),
      packetsReceived: getFirstValue(
        statsData.audio?.inbound,
        'packetsReceived'
      ),
    },
    remoteInboundRtpVideo: {
      jitter: getFirstValue(statsData.remote?.video?.inbound, 'jitter'),
      roundTripTime: getFirstValue(
        statsData.remote?.video?.inbound,
        'roundTripTime'
      ),
    },
    remoteInboundRtpAudio: {
      jitter: getFirstValue(statsData.remote?.audio?.inbound, 'jitter'),
      roundTripTime: getFirstValue(
        statsData.remote?.audio?.inbound,
        'roundTripTime'
      ),
    },
    outboundRtpVideo: {
      qualityLimitationReason: getFirstValue(
        statsData.video?.outbound,
        'qualityLimitationReason'
      ),
    },
  };
};

const calculatePacketLossRate = (
  packetsLost: number,
  packetsReceived: number
): number => {
  const totalPackets = packetsLost + packetsReceived;
  return totalPackets > 0 ? (packetsLost / totalPackets) * 100 : 0;
};

const extractVideoResolution = (
  statsReport: RTCStatsReport
): { inbound: string; outbound: string } => {
  let inboundRes = 'Unknown';
  let outboundRes = 'Unknown';

  for (const stat of statsReport.values()) {
    if (stat.type === 'inbound-rtp' && stat.kind === 'video') {
      if (stat.frameWidth && stat.frameHeight) {
        inboundRes = `${stat.frameWidth}x${stat.frameHeight}`;
      }
    } else if (stat.type === 'outbound-rtp' && stat.kind === 'video') {
      if (stat.frameWidth && stat.frameHeight) {
        outboundRes = `${stat.frameWidth}x${stat.frameHeight}`;
      }
    }
  }

  return { inbound: inboundRes, outbound: outboundRes };
};

const extractOutboundVideoFps = (statsReport: RTCStatsReport): number => {
  for (const stat of statsReport.values()) {
    if (stat.type === 'outbound-rtp' && stat.kind === 'video') {
      return stat.framesPerSecond || 0;
    }
  }
  return 0;
};

export const transformMetricsToLiveStats = async (
  peerId: string,
  metrics: Record<string, number | string | undefined>,
  rawStats: RTCStatsReport | null
): Promise<LiveConnectionStats> => {
  const videoPacketsLost = (metrics.videoPacketsLost as number) || 0;
  const videoPacketsReceived = (metrics.videoPacketsReceived as number) || 0;
  const audioPacketsLost = (metrics.audioPacketsLost as number) || 0;
  const audioPacketsReceived = (metrics.audioPacketsReceived as number) || 0;

  // Convert jitter and RTT from seconds to milliseconds for display
  const videoJitter = ((metrics.videoJitter as number) || 0) * 1000;
  const audioJitter = ((metrics.audioJitter as number) || 0) * 1000;
  const roundTripTime =
    ((metrics.videoRoundTripTime as number) ||
      (metrics.audioRoundTripTime as number) ||
      0) * 1000;

  let inboundVideoResolution = 'Unknown';
  let outboundVideoResolution = 'Unknown';
  let videoBitrate = 0;
  let outboundVideoFps = (metrics.videoFramesPerSecond as number) || 0;

  // Get bitrate from metrics (already converted to kbps)
  videoBitrate = (metrics.videoBitrate as number) || 0;

  if (rawStats) {
    const resolutions = extractVideoResolution(rawStats);
    inboundVideoResolution = resolutions.inbound;
    outboundVideoResolution = resolutions.outbound;
    outboundVideoFps = extractOutboundVideoFps(rawStats);
  }

  const packetLossRate = calculatePacketLossRate(
    videoPacketsLost + audioPacketsLost,
    videoPacketsReceived + audioPacketsReceived
  );

  const result = {
    peerId,
    inboundVideoFps: (metrics.videoFramesPerSecond as number) || 0,
    outboundVideoFps,
    videoJitter,
    audioJitter,
    roundTripTime,
    packetLossRate,
    videoBitrate,
    inboundVideoResolution,
    outboundVideoResolution,
    videoPacketsLost,
    videoPacketsReceived,
    audioPacketsLost,
    audioPacketsReceived,
  };

  return result;
};
